'use client'

import type { ActionResult } from '@mentora/app-shared/i18n/action-result'
import { useActionToast } from '@mentora/app-shared/i18n/use-action-toast'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Textarea,
} from '@mentora/ui-components'
import { Crown, Mail, Trash2 } from 'lucide-react'
import { useFormatter, useTranslations } from 'next-intl'
import { useState, useTransition } from 'react'

interface VipEntry {
  id: string
  email: string
  createdAt: string
}

export interface VipListPanelActions {
  addVipEmails: (input: {
    courseId: string
    emails: string
  }) => Promise<ActionResult<{ added: number, skipped: number, invalid: string[] }>>
  removeVipAccess: (input: {
    courseId: string
    vipId: string
  }) => Promise<ActionResult>
}

export interface VipListPanelProps {
  courseId: string
  entries: VipEntry[]
  /** The storefront's own server actions — the one thing the two copies of this screen never shared. */
  actions: VipListPanelActions
}

export function VipListPanel({ courseId, entries, actions }: VipListPanelProps) {
  const t = useTranslations('courses.vip')
  const format = useFormatter()
  const { showError, showSuccess } = useActionToast()
  const [isPending, startTransition] = useTransition()
  const [emails, setEmails] = useState('')

  function onAdd(e: React.FormEvent) {
    e.preventDefault()
    startTransition(async () => {
      const result = await actions.addVipEmails({ courseId, emails })
      if (!result.ok) {
        showError(result)
        return
      }
      const { added, invalid } = result.data!
      showSuccess(t('toasts.added', { count: added }))
      if (invalid.length > 0)
        showError({ ok: false, errorKey: 'courses.vip.toasts.someInvalid', errorValues: { count: invalid.length } })
      setEmails('')
    })
  }

  function onRemove(vipId: string) {
    startTransition(async () => {
      const result = await actions.removeVipAccess({ courseId, vipId })
      if (!result.ok) {
        showError(result)
        return
      }
      showSuccess(t('toasts.removed'))
    })
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Crown className="size-5 text-muted-foreground" />
          <CardTitle>{t('title')}</CardTitle>
        </div>
        <CardDescription>{t('subtitle')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <form onSubmit={onAdd} className="flex flex-col gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="vip-emails">{t('emailsLabel')}</Label>
            <Textarea
              id="vip-emails"
              rows={3}
              value={emails}
              onChange={e => setEmails(e.target.value)}
              placeholder={t('emailsPlaceholder')}
            />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={isPending || !emails.trim()}>
              {isPending ? t('adding') : t('addButton')}
            </Button>
          </div>
        </form>

        {entries.length === 0
          ? (
              <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border/70 p-8 text-center">
                <p className="font-medium text-foreground">{t('emptyTitle')}</p>
                <p className="text-sm text-muted-foreground">{t('emptySubtitle')}</p>
              </div>
            )
          : (
              <ul className="flex flex-col divide-y divide-border/60 rounded-lg border border-border/60">
                {entries.map(entry => (
                  <li
                    key={entry.id}
                    className="flex items-center justify-between gap-3 p-3"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <Mail className="size-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0">
                        <p className="truncate font-medium text-foreground">
                          {entry.email}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('grantedOn', {
                            date: format.dateTime(new Date(entry.createdAt), {
                              dateStyle: 'medium',
                            }),
                          })}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={isPending}
                      aria-label={t('remove')}
                      onClick={() => onRemove(entry.id)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
      </CardContent>
    </Card>
  )
}
